/**
 * PlayerCard — Contact card overlay
 *
 * Shows player profile with Chat, Remove, Block actions.
 * Displayed as an overlay within the crew modal content area.
 * Can also be used standalone in the future.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { formatStacks } from './formatStacks';
import { ImageViewer } from '../ImageViewer';

export interface PlayerCardData {
  id: string;
  username: string;
  picture: string;
  /** Soft-currency balance (Stacks) — shown in place of level. */
  stacks: number;
  level?: number;
  crewTag?: string;
  isOnline: boolean;
  isFriend: boolean;
  isBlocked: boolean;
  isPending?: boolean;
}

interface PlayerCardEvents {
  'action:chat': { playerId: string };
  'action:remove': { playerId: string };
  'action:block': { playerId: string };
  'action:unblock': { playerId: string };
  'action:add': { playerId: string };
  'action:close': void;
}

export class PlayerCard extends EventEmitter<PlayerCardEvents> {
  public element: HTMLElement;
  private data: PlayerCardData;
  private confirmingAction: string | null = null;

  constructor(data: PlayerCardData) {
    super();
    this.data = data;
    this.element = this.createElement();
  }

  show(): void {
    this.element.classList.add('visible');
  }

  /**
   * Live-update the presence dot without re-rendering the card (which would
   * clobber the remove/block confirm state). Driven by friend:online/offline.
   */
  setOnline(isOnline: boolean): void {
    if (this.data.isOnline === isOnline) return;
    this.data.isOnline = isOnline;
    const wrap = this.element.querySelector('.crew-card-avatar-wrap');
    if (!wrap) return;
    wrap.classList.toggle('online', isOnline);
    const existing = wrap.querySelector('.crew-card-online-dot');
    if (isOnline && !existing) {
      const dot = document.createElement('span');
      dot.className = 'crew-card-online-dot';
      wrap.appendChild(dot);
    } else if (!isOnline && existing) {
      existing.remove();
    }
  }

  hide(): void {
    this.element.classList.remove('visible');
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'crew-player-card';
    this.render(el);

    el.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest<HTMLElement>('[data-action]');

      // Click backdrop to close
      if (target === el.querySelector('.crew-card-backdrop')) {
        this.emit('action:close', undefined);
        return;
      }

      // Tap the avatar photo → full-screen pinch-to-zoom viewer.
      if (target.closest('.crew-card-avatar-img')) {
        ImageViewer.open(this.data.picture);
        return;
      }

      if (!btn) return;
      const action = btn.dataset.action!;

      // Handle confirm flow for destructive actions
      if (action === 'remove' || action === 'block') {
        if (this.confirmingAction === action) {
          // Second tap = confirmed
          this.emit(`action:${action}` as keyof PlayerCardEvents, { playerId: this.data.id });
          this.confirmingAction = null;
          return;
        }
        // First tap = show confirm state
        this.confirmingAction = action;
        btn.classList.add('confirming');
        btn.textContent = action === 'remove' ? 'TAP TO CONFIRM' : 'CONFIRM BLOCK';
        setTimeout(() => {
          this.confirmingAction = null;
          this.render(el);
        }, 3000);
        return;
      }

      if (action === 'chat') {
        // Friends-only: never let someone DM a stranger. The button is also
        // rendered `disabled` when not a friend, but guard here too in case a
        // click slips through (e.g. on the icon/label inside it).
        if (!this.data.isFriend || this.data.isBlocked) return;
        this.emit('action:chat', { playerId: this.data.id });
      } else if (action === 'add') {
        this.emit('action:add', { playerId: this.data.id });
        this.data.isPending = true;
        this.render(el);
      } else if (action === 'unblock') {
        this.emit('action:unblock', { playerId: this.data.id });
      } else if (action === 'close') {
        this.emit('action:close', undefined);
      }
    });

    return el;
  }

  private render(el: HTMLElement): void {
    const { username, picture, stacks, crewTag, isOnline, isFriend, isBlocked, isPending } = this.data;
    // Chat is for crew only — strangers can't be messaged.
    const canChat = isFriend && !isBlocked;
    const initial = username.charAt(0).toUpperCase();
    const avatarContent = picture
      ? `<img src="${picture}" alt="${initial}" class="crew-card-avatar-img">`
      : `<span class="crew-card-avatar-initial">${initial}</span>`;

    el.innerHTML = `
      <div class="crew-card-backdrop"></div>
      <div class="crew-card-panel">
        <button class="crew-card-close" data-action="close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div class="crew-card-avatar-wrap ${isOnline ? 'online' : ''}">
          <div class="crew-card-avatar-inner">
            ${avatarContent}
          </div>
          ${isOnline ? '<span class="crew-card-online-dot"></span>' : ''}
        </div>

        <div class="crew-card-username">${username}</div>
        <div class="crew-card-meta"><span class="crew-cell-stacks">$${formatStacks(stacks ?? 0)}</span>${crewTag ? ` · [${crewTag}]` : ''}</div>

        <div class="crew-card-actions" data-sound="button">
          ${isBlocked
            ? `<button class="crew-card-btn crew-card-btn-unblock" data-action="unblock">
                 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                   <path d="M18.36 6.64A9 9 0 1 1 5.64 19.36"/>
                   <line x1="2" y1="2" x2="22" y2="22"/>
                 </svg>
                 UNBLOCK
               </button>`
            : `<button class="crew-card-btn crew-card-btn-chat${canChat ? '' : ' crew-card-btn-disabled'}" data-action="chat"${canChat ? '' : ' disabled'}>
                 ${canChat
                   ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>`
                   : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>`}
                 CHAT
               </button>
               ${isFriend
                 ? `<button class="crew-card-btn crew-card-btn-remove" data-action="remove">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="8.5" cy="7" r="4"/>
                        <line x1="18" y1="11" x2="23" y2="11"/>
                      </svg>
                      REMOVE
                    </button>`
                 : isPending
                   ? `<button class="crew-card-btn crew-card-btn-pending" disabled>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        PENDING
                      </button>`
                   : `<button class="crew-card-btn crew-card-btn-add" data-action="add">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="8.5" cy="7" r="4"/>
                          <line x1="20" y1="8" x2="20" y2="14"/>
                          <line x1="23" y1="11" x2="17" y2="11"/>
                        </svg>
                        ADD
                      </button>`
               }
               <button class="crew-card-btn crew-card-btn-block" data-action="block">
                 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                   <circle cx="12" cy="12" r="10"/>
                   <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                 </svg>
                 BLOCK
               </button>`
          }
        </div>
        ${isBlocked
          ? `<div class="crew-card-chat-hint">You blocked this player</div>`
          : (canChat ? '' : `<div class="crew-card-chat-hint">Add to your crew to chat</div>`)}
      </div>
    `;
  }
}
